import { ChevronDownIcon } from '@orn/shared-web/icons';
import { cx, sortCx } from '@orn/shared-web/utils/cx';
import * as React from 'react';
import { Link as RouterLink } from 'react-router-dom';

const styles = sortCx({
  root: 'group relative flex w-full cursor-pointer items-center rounded-md outline-focus-ring transition duration-200 ease-linear select-none focus-visible:z-10 focus-visible:bg-navigation-active_focused focus-visible:outline-2 focus-visible:outline-offset-2',
  rootSelected: 'bg-navigation-active hover:bg-navigation-active_hover',
  rootNotSelected: 'hover:bg-navigation-secondary_hover',
});

interface INavItemBaseProps {
  /** Whether the nav item shows only an icon. */
  iconOnly?: boolean;
  /** URL to navigate to when the nav item is clicked. */
  href?: string;
  /** Type of the nav item. `button` is for rows that open a surface in place rather than navigating. */
  type: 'link' | 'button' | 'collapsible' | 'collapsible-child';
  /** Icon component to display. */
  icon?: React.FC<React.HTMLAttributes<HTMLOrSVGElement>>;
  /** Whether the nav item is currently active. */
  current?: boolean;
  /** Whether to truncate the label text. */
  truncate?: boolean;
  /** Handler for click events. */
  onClick?: React.MouseEventHandler;
  /** Content to display. */
  children?: React.ReactNode;
}

/** Navigation item supporting link, collapsible section, and collapsible child variants. Applies active/hover/focused background and foreground color states. */
const NavItem = ({ current, type, href, icon: Icon, children, truncate = true, onClick }: INavItemBaseProps) => {
  const iconElement = Icon && (
    <Icon
      aria-hidden="true"
      className={cx(
        'text-fg-quaternary transition-inherit-all mr-2 size-5 shrink-0',
        current
          ? 'text-fg-brand-primary'
          : 'group-hover:text-fg-quaternary_hover group-focus-visible:text-fg-brand-primary'
      )}
    />
  );

  const labelElement = (
    <span
      className={cx(
        'text-md text-secondary transition-inherit-all group-hover:text-secondary_hover group-focus-visible:text-brand-primary flex-1 font-semibold',
        truncate && 'truncate',
        current && 'text-secondary_hover'
      )}
    >
      {children}
    </span>
  );

  if (type === 'collapsible') {
    return (
      <summary
        className={cx('px-3 py-2', styles.root, current ? styles.rootSelected : styles.rootNotSelected)}
        onClick={onClick}
      >
        {iconElement}

        {labelElement}

        <ChevronDownIcon
          aria-hidden="true"
          className="text-fg-quaternary group-hover:text-fg-quaternary_hover group-focus-visible:text-fg-brand-primary in-open:-scale-y-100 ml-3 size-4 shrink-0 stroke-[2.5px]"
        />
      </summary>
    );
  }

  if (type === 'button') {
    return (
      <button
        type="button"
        onClick={onClick}
        className={cx('px-3 py-2 text-left', styles.root, current ? styles.rootSelected : styles.rootNotSelected)}
      >
        {iconElement}
        {labelElement}
      </button>
    );
  }

  if (type === 'collapsible-child') {
    return (
      <RouterLink
        to={href || '#'}
        className={cx('py-2 pl-10 pr-3', styles.root, current ? styles.rootSelected : styles.rootNotSelected)}
        onClick={onClick}
        aria-current={current ? 'page' : undefined}
      >
        {labelElement}
      </RouterLink>
    );
  }

  return (
    <RouterLink
      to={href || '#'}
      className={cx('px-3 py-2', styles.root, current ? styles.rootSelected : styles.rootNotSelected)}
      onClick={onClick}
      aria-current={current ? 'page' : undefined}
    >
      {iconElement}
      {labelElement}
    </RouterLink>
  );
};

export default NavItem;
